import React from 'react';
import { useUnifiedAuth } from '../auth/AuthProvider';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Enforce HTTPS in production with better logging
const getApiBaseUrl = () => {
  const isProduction = window.location.protocol === 'https:';
  let apiUrl = API_BASE_URL;
  
  // Debug logging to help identify configuration issues
  console.log('🔍 API Base URL Configuration:', {
    originalUrl: API_BASE_URL,
    isHttpsContext: isProduction,
    windowLocation: window.location.protocol
  });
  
  if (isProduction && apiUrl.startsWith('http://')) {
    apiUrl = apiUrl.replace('http://', 'https://');
    console.log('🔒 Upgraded API URL to HTTPS:', apiUrl);
  }
  
  // Validate final URL
  if (isProduction && !apiUrl.startsWith('https://')) {
    console.error('❌ API URL is not HTTPS in production context!', apiUrl);
  }
  
  return apiUrl;
};

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  error: string;
  message: string;
  details?: string;
}

export interface AppData {
  app_id: string;
  client_id?: string;
  name: string;
  description?: string;
  logo_uri?: string;
  app_type?: string;
  app_type_display?: string;
  is_active: boolean;
  is_enabled: boolean;
  feedback_count: number;
  last_feedback_at?: string;
  created_at?: string;
  updated_at?: string;
  last_synced_at?: string;
  sync_version: number;
  display_name?: string;
  callback_urls?: string[];
  web_origins?: string[];
  allowed_origins?: string[];
  app_metadata?: Record<string, any>;
  client_metadata?: Record<string, any>;
}

class ApiService {
  private getAccessToken: (() => Promise<string>) | null = null;

  // Set the token getter function (called from useApiService hook)
  setTokenGetter(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get access token:', error);
        throw new Error('Authentication failed');
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      // Debug logging
      console.log('🔍 API Request Details:', {
        url,
        method: options.method || 'GET',
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Handle redirect responses specifically
      if (response.status >= 300 && response.status < 400) {
        const location = response.headers.get('location');
        console.error('🔄 API Redirect Response:', {
          status: response.status,
          statusText: response.statusText,
          location,
          originalUrl: url,
          redirectType: response.status === 307 ? 'Temporary Redirect' : 'Other Redirect'
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error Response:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ API Response Data:', data);
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Health check endpoint
  async checkHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health/');
  }

  // Feedback endpoints
  async getFeedback(params?: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
  }): Promise<ApiResponse<any[]>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);

    const queryString = searchParams.toString();
    return this.request(`/api/v1/feedback${queryString ? `?${queryString}` : ''}`);
  }


  // Feedback stats
  async getFeedbackStats(): Promise<ApiResponse<{
    total_feedback: number;
    active_feedback: number;
    total_applications: number;
    pending_feedback: number;
  }>> {
    return this.request('/api/v1/feedback/stats/overview');
  }

  // App management endpoints
  async getApps(params?: {
    app_type?: string;
    active_only?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    apps: any[];
    pagination: {
      total: number;
      page: number;
      pages: number;
      per_page: number;
      has_next: boolean;
      has_prev: boolean;
    };
    filters: any;
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.app_type) searchParams.append('app_type', params.app_type);
    if (params?.active_only) searchParams.append('active_only', params.active_only.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    return this.request(`/api/v1/apps/${queryString ? `?${queryString}` : ''}`);
  }

  async getAppById(appId: string, includeMetadata?: boolean): Promise<ApiResponse<any>> {
    const searchParams = new URLSearchParams();
    if (includeMetadata) searchParams.append('include_metadata', 'true');
    
    const queryString = searchParams.toString();
    return this.request(`/api/v1/apps/${appId}${queryString ? `?${queryString}` : ''}`);
  }

  async syncApps(clientIds: string[], forceUpdate = false): Promise<ApiResponse<{
    total_apps: number;
    synced_apps: number;
    created_apps: number;
    updated_apps: number;
    failed_apps: number;
    not_found_apps: number;
    errors: any[];
  }>> {
    return this.request('/api/v1/apps/sync', {
      method: 'POST',
      body: JSON.stringify({
        client_ids: clientIds,
        force_update: forceUpdate
      }),
    });
  }

  async syncAllAppsFromTenant(forceUpdate = false, maxApps = 1000): Promise<ApiResponse<{
    total_apps: number;
    synced_apps: number;
    created_apps: number;
    updated_apps: number;
    failed_apps: number;
    not_found_apps: number;
    errors: any[];
  }>> {
    const searchParams = new URLSearchParams();
    searchParams.append('force_update', forceUpdate.toString());
    searchParams.append('max_apps', maxApps.toString());
    
    return this.request(`/api/v1/apps/sync/all?${searchParams.toString()}`, {
      method: 'POST',
    });
  }

  async getAppStats(): Promise<ApiResponse<{
    overview: {
      total_apps: number;
      active_apps: number;
      by_type: Record<string, number>;
      total_feedback: number;
      avg_feedback_per_app: number;
    };
    generated_at: string;
  }>> {
    return this.request('/api/v1/apps/stats/overview');
  }

  async getMostActiveApps(limit = 10): Promise<ApiResponse<any[]>> {
    const searchParams = new URLSearchParams();
    searchParams.append('limit', limit.toString());
    
    return this.request(`/api/v1/apps/active/top?${searchParams.toString()}`);
  }

  async updateApp(appId: string, updates: {
    name?: string;
    description?: string;
    logo_uri?: string;
    is_active?: boolean;
    is_enabled?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/apps/${appId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteApp(appId: string): Promise<ApiResponse<{
    message: string;
    app_id: string;
    deleted_at: string;
  }>> {
    return this.request(`/api/v1/apps/${appId}`, {
      method: 'DELETE',
    });
  }

  async recalculateFeedbackCounts(): Promise<ApiResponse<{
    message: string;
    updated_apps: number;
    updated_counts: Record<string, number>;
    timestamp: string;
  }>> {
    return this.request('/api/v1/apps/recalculate-feedback-counts', {
      method: 'POST',
    });
  }

  // Mock feedback endpoints for testing
  async generateMockFeedback(count: number = 1): Promise<ApiResponse<any>> {
    if (count === 1) {
      return this.request('/api/v1/mock-feedback/single', {
        method: 'POST',
      });
    } else {
      return this.request(`/api/v1/mock-feedback/batch?count=${count}`, {
        method: 'POST',
      });
    }
  }

  async getMockFeedbackConfig(): Promise<ApiResponse<any>> {
    return this.request('/api/v1/mock-feedback/config');
  }

  async testWebSocket(): Promise<ApiResponse<any>> {
    return this.request('/api/v1/mock-feedback/test-websocket', {
      method: 'POST',
    });
  }

  // Feedback endpoints
  async getFeedbackList(params: {
    page?: number;
    per_page?: number;
    app_id?: string;
    user_id?: string;
    status?: string;
    priority?: string;
    assigned_to?: string;
    include_comments?: boolean;
    include_user?: boolean;
    include_app?: boolean;
  } = {}): Promise<ApiResponse<{
    items: any[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  }>> {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.app_id) searchParams.append('app_id', params.app_id);
    if (params.user_id) searchParams.append('user_id', params.user_id);
    if (params.status) searchParams.append('status', params.status);
    if (params.priority) searchParams.append('priority', params.priority);
    if (params.assigned_to) searchParams.append('assigned_to', params.assigned_to);
    if (params.include_comments) searchParams.append('include_comments', params.include_comments.toString());
    if (params.include_user) searchParams.append('include_user', params.include_user.toString());
    if (params.include_app) searchParams.append('include_app', params.include_app.toString());
    
    // Add trailing slash to fix 307 redirects
    const queryString = searchParams.toString();
    return this.request(`/api/v1/feedback/${queryString ? `?${queryString}` : ''}`);
  }

  async getFeedbackById(feedbackId: number, params: {
    include_comments?: boolean;
    include_user?: boolean;
    include_app?: boolean;
  } = {
    include_user: true,
    include_app: true
  }): Promise<ApiResponse<any>> {
    const searchParams = new URLSearchParams();
    
    if (params.include_comments) searchParams.append('include_comments', params.include_comments.toString());
    if (params.include_user) searchParams.append('include_user', params.include_user.toString());
    if (params.include_app) searchParams.append('include_app', params.include_app.toString());
    
    return this.request(`/api/v1/feedback/${feedbackId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`);
  }

  async createFeedback(feedbackData: {
    title: string;
    description: string;
    app_client_id?: string;
    priority?: string;
    external_id?: string;
    source_url?: string;
    extra_data?: any;
  }): Promise<ApiResponse<any>> {
    return this.request('/api/v1/feedback/', {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    });
  }

  async updateFeedback(feedbackId: number, updateData: {
    title?: string;
    description?: string;
    status?: string;
    priority?: string;
    assigned_to?: string;
    extra_data?: any;
  }): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/feedback/${feedbackId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteFeedback(feedbackId: number): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/feedback/${feedbackId}`, {
      method: 'DELETE',
    });
  }

  // Comment endpoints
  async createComment(commentData: {
    feedback_id: number;
    content: string;
    parent_id?: number;
    is_internal?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('/api/v1/comments/', {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  async getCommentsForFeedback(feedbackId: number, params: {
    include_internal?: boolean;
    include_replies?: boolean;
    page?: number;
    per_page?: number;
  } = {}): Promise<ApiResponse<{
    items: any[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  }>> {
    const searchParams = new URLSearchParams();
    
    if (params.include_internal) searchParams.append('include_internal', params.include_internal.toString());
    if (params.include_replies) searchParams.append('include_replies', params.include_replies.toString());
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    
    return this.request(`/api/v1/comments/feedback/${feedbackId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`);
  }

  async getComment(commentId: number): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/comments/${commentId}`);
  }

  async updateComment(commentId: number, updateData: {
    content?: string;
    is_internal?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteComment(commentId: number): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // Generic request method for custom endpoints
  async customRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return this.request(endpoint, options);
  }

  // Settings endpoints for auth provider management
  async getAuthProviders(): Promise<ApiResponse<{
    auth0: {
      enabled: boolean;
      domain?: string;
      audience?: string;
      management_client_id?: string;
    };
    authentik: {
      enabled: boolean;
      url?: string;
      client_id?: string;
      app_slug?: string;
    };
    active_providers: string[];
    updated_at?: string;
  }>> {
    return this.request('/api/v1/settings/auth-providers');
  }

  async updateAuthProviders(updates: {
    auth0?: {
      enabled?: boolean;
      domain?: string;
      audience?: string;
      management_client_id?: string;
      management_client_secret?: string;
    };
    authentik?: {
      enabled?: boolean;
      url?: string;
      client_id?: string;
      app_slug?: string;
      api_token?: string;
    };
  }): Promise<ApiResponse<any>> {
    return this.request('/api/v1/settings/auth-providers', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async testAuthProvider(provider: 'auth0' | 'authentik'): Promise<ApiResponse<{
    provider: string;
    success: boolean;
    message: string;
    details?: any;
    response_time_ms?: number;
    tested_at: string;
  }>> {
    return this.request(`/api/v1/settings/auth-providers/test/${provider}`, {
      method: 'POST',
    });
  }

  async getEnabledAuthProviders(): Promise<ApiResponse<{
    providers: Array<{
      type: string;
      name: string;
      domain?: string;
      audience?: string;
      url?: string;
      client_id?: string;
      app_slug?: string;
    }>;
    default_provider?: string;
  }>> {
    // This endpoint is public (no auth required)
    const url = `${getApiBaseUrl()}/api/v1/settings/auth-providers/enabled`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    return { data };
  }
}

// Create a singleton instance
export const apiService = new ApiService();

// React hook for using the API service with auth token
export const useApiService = () => {
  const { getAccessTokenSilently } = useUnifiedAuth();

  // Set up the token getter function
  React.useEffect(() => {
    apiService.setTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  return apiService;
}; 